import { useState, FormEvent } from 'react';
import { apiService } from '../services/api';

interface MFAResetFlowProps {
  onComplete: () => void;
  onBack: () => void;
}

type Step = 'request' | 'verify' | 'success';

export default function MFAResetFlow({ onComplete, onBack }: MFAResetFlowProps) {
  const [step, setStep] = useState<Step>('request');
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!usernameOrEmail.trim()) {
      setError('Please enter your username or email');
      return;
    }

    setIsLoading(true);

    try {
      await apiService.requestMFAReset({ identifier: usernameOrEmail.trim() });
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request MFA reset');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!code.trim() || code.length !== 6) {
      setError('Please enter the 6-digit code from your email');
      return;
    }

    setIsLoading(true);

    try {
      // Step 1: Verify email reset code and get QR code
      await apiService.verifyMFAReset({
        identifier: usernameOrEmail.trim(),
        reset_code: code.trim(),
      });
      // TODO: MFA reset flow needs to display QR code and collect new TOTP code
      // Step 2: Complete reset with new TOTP code from authenticator app
      await apiService.confirmMFAReset({
        identifier: usernameOrEmail.trim(),
        reset_code: code.trim(),
        new_totp_code: '', // This needs to be collected from user after showing QR code
      });
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">MFA Reset Complete</h2>
          <p className="text-gray-600 text-sm mb-6">
            Your MFA has been reset. Log in again to set up MFA.
          </p>
          <button
            onClick={onComplete}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Verify MFA Reset</h2>
        <p className="text-gray-600 text-sm mb-6">
          We've sent a verification code to your email. Please enter it below.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleVerifySubmit} className="space-y-4">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
              Verification Code
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest"
              placeholder="000000"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Verifying...' : 'Reset MFA'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => {
              setStep('request');
              setCode('');
              setError('');
            }}
            className="text-sm text-gray-600 hover:underline"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Reset MFA</h2>
      <p className="text-gray-600 text-sm mb-6">
        Lost access to your authenticator app? Enter your username or email to receive a reset code.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleRequestSubmit} className="space-y-4">
        <div>
          <label htmlFor="usernameOrEmail" className="block text-sm font-medium text-gray-700 mb-1">
            Username or Email
          </label>
          <input
            id="usernameOrEmail"
            type="text"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your username or email"
            autoFocus
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || !usernameOrEmail.trim()}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Sending...' : 'Send Reset Code'}
        </button>
      </form>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-600 hover:underline"
        >
          Back to login
        </button>
      </div>
    </div>
  );
}
